import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { Value } from 'slate';
import unified from 'unified';
import markdown from 'remark-parse';
import flatten from 'lodash/flatten';
import BlockTypes, { HEADER_BLOCKS } from '../../../types/BlockTypes';
import EntityTypes from '../../../types/EntityTypes';
import StyleTypes from '../../../types/StyleTypes';
import generateBlockId from '../../utils/generateBlockId';
import { LINE_BREAK_CHARACTER } from '../../constants';
import languages, { NO_SYNTAX } from '../../CodePlugin/languages';
import processSliteUrl from '../../utils/processSliteUrl';
import { INLINE, TEXT, DOCUMENT, BLOCK } from '../../../types/SlateTypes';
var SIMPLE_BLOCKS = ['paragraph', 'code'];
var STYLE_TO_MARK_MAP = {
  strong: {
    object: 'mark',
    type: StyleTypes.BOLD
  },
  emphasis: {
    object: 'mark',
    type: StyleTypes.ITALIC
  },
  inlineCode: {
    object: 'mark',
    type: StyleTypes.CODE
  },
  "delete": {
    object: 'mark',
    type: StyleTypes.STRIKETHROUGH
  }
};
var STYLE_TYPES = Object.keys(STYLE_TO_MARK_MAP);
var TABLE_ID;

function createBlock(type, node) {
  var data = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return {
    object: BLOCK,
    type: type,
    data: _objectSpread({
      id: generateBlockId()
    }, data),
    nodes: toNodes(node.children || [], node)
  };
}

function createInline(type, node) {
  var data = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return {
    object: INLINE,
    type: type,
    data: _objectSpread({
      entityId: generateBlockId()
    }, data),
    nodes: toNodes(node.children || [], node)
  };
}

function createLineBreak() {
  return {
    object: INLINE,
    type: EntityTypes.LINE_BREAK,
    data: {
      entityId: generateBlockId()
    },
    nodes: [{
      object: TEXT,
      text: LINE_BREAK_CHARACTER
    }]
  };
}

function createText(text) {
  var marks = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return flatten(text.split('\n').map(function (text, index) {
    return [].concat(_toConsumableArray(index !== 0 ? [createLineBreak()] : []), [{
      object: TEXT,
      text: text,
      marks: marks
    }]);
  }));
}

var MARKDOWN = {
  ROOT: 'root',
  HEADER: 'heading',
  QUOTE: 'blockquote',
  IMAGE: 'image',
  CODE: 'code',
  TABLE: 'table',
  TABLE_ROW: 'tableRow',
  TABLE_CELL: 'tableCell',
  LIST: 'list',
  LIST_ITEM: 'listItem',
  SEPARATOR: 'thematicBreak',
  UNSTYLED: 'paragraph',
  LINK: 'link',
  TEXT: 'text'
};
var SUPPORTED_NODES = Object.values(MARKDOWN);

function isTodo(node) {
  return typeof node.checked === 'boolean' || typeof (node.children[0] || {}).checked === 'boolean';
}

function toNode(node) {
  var parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var index = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var marks = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];

  if (node.type === MARKDOWN.ROOT) {
    return {
      object: DOCUMENT,
      nodes: toNodes(node.children, node)
    };
  }

  if (node.type === MARKDOWN.TEXT) {
    return createText(node.value, marks);
  }

  if (node.type === MARKDOWN.LIST) {
    if (isTodo(node)) {
      return createBlock(BlockTypes.TODOS, node);
    } else if (node.ordered) {
      return createBlock(BlockTypes.OL, node);
    } else {
      return createBlock(BlockTypes.UL, node);
    }
  } else if (node.type === MARKDOWN.LIST_ITEM) {
    var depth = Math.floor((node.position.start.column - 1) / 2);

    if (isTodo(node)) {
      var checked = node.checked;
      return createBlock(BlockTypes.TODO, node, {
        checked: checked,
        depth: depth
      });
    } else if (parent.ordered) {
      return createBlock(BlockTypes.OL_LI, node, {
        depth: depth
      });
    } else {
      return createBlock(BlockTypes.UL_LI, node, {
        depth: depth
      });
    }
  } // Headers after H3 are treated as paragraphs later


  if (node.type === MARKDOWN.HEADER && node.depth <= 3) {
    return createBlock(HEADER_BLOCKS[node.depth - 1], node);
  }

  if (node.type === MARKDOWN.QUOTE) {
    return createBlock(BlockTypes.QUOTE, node);
  }

  if (node.type === MARKDOWN.IMAGE) {
    return createBlock(BlockTypes.IMAGE, node, {
      url: node.url
    });
  }

  if (node.type === MARKDOWN.SEPARATOR) {
    return createBlock(BlockTypes.SEPARATOR, node);
  }

  if (node.type === MARKDOWN.LINK) {
    var _processSliteUrl = processSliteUrl(node.url),
        noteId = _processSliteUrl.noteId,
        blockId = _processSliteUrl.blockId;

    if (noteId) {
      return createInline(EntityTypes.NOTELINK, node, {
        noteId: noteId,
        blockId: blockId
      });
    }

    return createInline(EntityTypes.LINK, node, {
      url: node.url
    });
  }

  if (STYLE_TYPES.includes(node.type)) {
    var mark = STYLE_TO_MARK_MAP[node.type];

    if (!node.children && node.value) {
      return createText(node.value, [].concat(_toConsumableArray(marks), [mark]));
    }

    return toNodes(node.children, parent, index, [].concat(_toConsumableArray(marks), [mark]));
  }

  if (node.type === MARKDOWN.TABLE) {
    TABLE_ID = generateBlockId();
    return createBlock(BlockTypes.TABLE, node);
  } else if (node.type === MARKDOWN.TABLE_ROW) {
    return node.children.map(function (cell, index) {
      return createBlock(BlockTypes.TABLE_CELL, cell, {
        tableId: TABLE_ID,
        rowBreak: index === node.children.length - 1
      });
    });
  }

  if (node.type === MARKDOWN.CODE) {
    var language = Object.prototype.hasOwnProperty.call(languages, node.lang) ? node.lang : NO_SYNTAX;
    return {
      object: BLOCK,
      type: BlockTypes.CODE,
      nodes: createText(node.value),
      data: {
        language: language
      }
    };
  }

  if ((node.type === MARKDOWN.UNSTYLED || node.children) && parent.type === MARKDOWN.ROOT) {
    return createBlock(BlockTypes.UNSTYLED, node);
  } else if (node.children) {
    return toNodes(node.children, parent, index, marks);
  }

  console.error("Unsupported markdown node `".concat(node.type, "`: %o"), node);
}

function toNodes(nodes) {
  var parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var startIndex = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var marks = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  return flatten(nodes.map(function (child, index) {
    return toNode(child, parent, index + startIndex, marks);
  }).filter(function (c) {
    return !!c;
  }));
}

function preProcessMarkdown(text) {
  return text.split('\n')
  /** This ensure checkboxes list are actually lists. This ensure
   * compatibility with different markdown specs.
   *
   * Before:
   *   [ ] Un-checked
   *   [x] Checked
   * After:
   *   - [ ] Un-checked
   *   - [x] Checked
   */
  .map(function (line) {
    return line.replace(/^( *)(\[([x ])?\]) /, '$1- $2 ');
  }).join('\n');
}

export default function makeMarkdownDeserializer(editor, event) {
  var cache; // Avoid parsing markdown twice

  return {
    canDeserialize: function canDeserialize(_ref) {
      var _ref$text = _ref.text,
          rawText = _ref$text === void 0 ? '' : _ref$text,
          _ref$html = _ref.html,
          html = _ref$html === void 0 ? '' : _ref$html;
      if (editor.isShiftKeyPressed()) return false;
      if (!rawText || html) return false;
      var processor = unified().use(markdown, {
        gfm: true
      });
      var text = preProcessMarkdown(rawText || '');
      cache = processor.parse(text);
      var isOnlySimpleBlocks = cache.children.every(function (child) {
        return SIMPLE_BLOCKS.includes(child.type);
      });
      var hasAtLeastOneSupportedBLock = cache.children.some(function (child) {
        return SUPPORTED_NODES.includes(child.type);
      });
      return !isOnlySimpleBlocks && hasAtLeastOneSupportedBLock;
    },
    deserialize: function deserialize() {
      return Value.create({
        document: toNode(cache)
      });
    }
  };
}